import React, { useState, useRef, useCallback, useEffect, memo } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  Popover,
  Button,
  Divider,
} from '@mui/material';
import { Functions as FunctionsIcon, Close as CloseIcon } from '@mui/icons-material';
import MathTextRenderer from './MathTextRenderer';
import 'mathlive';

// Type augmentation for MathLive web component
declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'math-field': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          ref?: React.Ref<any>;
          'virtual-keyboard-mode'?: string;
        },
        HTMLElement
      >;
    }
  }
}

interface MathInputProps {
  /** Current value (plain text with $...$ LaTeX delimiters) */
  value: string;
  /** Called when value changes */
  onChange: (value: string) => void;
  /** MUI TextField label */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Number of rows for multiline mode */
  rows?: number;
  /** Whether to show multiline */
  multiline?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Full width */
  fullWidth?: boolean;
  /** Additional sx */
  sx?: any;
  /** Whether field is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
  /** Helper text */
  helperText?: string;
}

const MathInput: React.FC<MathInputProps> = ({
  value,
  onChange,
  label,
  required = false,
  rows = 3,
  multiline = true,
  placeholder,
  fullWidth = true,
  sx,
  disabled = false,
  size,
  helperText,
}) => {
  const [mathEditorOpen, setMathEditorOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentLatex, setCurrentLatex] = useState('');
  const textFieldRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const mathFieldRef = useRef<any>(null);
  const cursorPositionRef = useRef<number>(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Track cursor position in the text field
  const handleSelect = useCallback(() => {
    if (textFieldRef.current) {
      cursorPositionRef.current = textFieldRef.current.selectionStart || 0;
    }
  }, []);

  // Open math editor popover
  const handleOpenMathEditor = useCallback((event: React.MouseEvent<HTMLElement>) => {
    // Save cursor position before opening
    if (textFieldRef.current) {
      cursorPositionRef.current = textFieldRef.current.selectionStart || value.length;
    }
    setAnchorEl(event.currentTarget);
    setMathEditorOpen(true);
    setCurrentLatex('');
  }, [value.length]);

  // Close math editor
  const handleCloseMathEditor = useCallback(() => {
    setMathEditorOpen(false);
    setAnchorEl(null);
    setCurrentLatex('');
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Insert the equation into the text at cursor position
  const handleInsertEquation = useCallback(() => {
    // Read value directly from the math-field element as a final check
    const mf = mathFieldRef.current;
    const latexToInsert = (mf?.value || currentLatex || '').trim();
    if (!latexToInsert) return;

    const pos = cursorPositionRef.current;
    const before = value.slice(0, pos);
    const after = value.slice(pos);

    // Add spaces around the equation for readability
    const spaceBefore = before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n') ? ' ' : '';
    const spaceAfter = after.length > 0 && !after.startsWith(' ') && !after.startsWith('\n') ? ' ' : '';

    const newValue = `${before}${spaceBefore}$${latexToInsert}$${spaceAfter}${after}`;
    onChange(newValue);

    // Update cursor position
    cursorPositionRef.current = pos + spaceBefore.length + latexToInsert.length + 2 + spaceAfter.length;

    handleCloseMathEditor();
  }, [currentLatex, value, onChange, handleCloseMathEditor]);

  // Poll the math-field value to keep currentLatex in sync.
  // MathLive web component events can be unreliable with React, so polling
  // the `.value` property is the most robust approach.
  useEffect(() => {
    if (mathEditorOpen) {
      // Wait a bit for the element to mount, then start polling
      const startTimer = setTimeout(() => {
        const mf = mathFieldRef.current;
        if (mf?.focus) mf.focus();

        pollIntervalRef.current = setInterval(() => {
          const el = mathFieldRef.current;
          if (el && el.value !== undefined) {
            const val = el.value || '';
            setCurrentLatex((prev: string) => (prev !== val ? val : prev));
          }
        }, 200);
      }, 150);

      return () => {
        clearTimeout(startTimer);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };
    }
  }, [mathEditorOpen]);

  // Handle Enter key in math field to insert
  const handleMathFieldKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleInsertEquation();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCloseMathEditor();
    }
  }, [handleInsertEquation, handleCloseMathEditor]);

  const hasPreviewContent = value && value.includes('$');

  return (
    <Box sx={{ ...(fullWidth && { width: '100%', flex: 1, minWidth: 0 }), ...sx }}>
      {/* Main text field with math toolbar */}
      <Box sx={{ position: 'relative' }}>
        <TextField
          inputRef={textFieldRef}
          fullWidth={fullWidth}
          label={label}
          required={required}
          multiline={multiline}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onSelect={handleSelect}
          placeholder={placeholder || (multiline ? 'Type your text here. Use the 𝑓(x) button to insert math equations...' : undefined)}
          disabled={disabled}
          size={size}
          helperText={helperText}
          InputProps={{
            endAdornment: (
              <Tooltip title="Insert Math Equation" arrow>
                <IconButton
                  onClick={handleOpenMathEditor}
                  disabled={disabled}
                  size="small"
                  sx={{
                    alignSelf: multiline ? 'flex-start' : 'center',
                    mt: multiline ? 0.5 : 0,
                    color: 'primary.main',
                    bgcolor: 'action.hover',
                    '&:hover': {
                      bgcolor: 'primary.main',
                      color: 'white',
                    },
                    borderRadius: 1,
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    fontStyle: 'italic',
                    width: 32,
                    height: 32,
                  }}
                >
                  <FunctionsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
      </Box>

      {/* Live preview (only shown when LaTeX is present) */}
      {hasPreviewContent && (
        <Paper
          variant="outlined"
          sx={{
            mt: 1,
            p: 1.5,
            bgcolor: 'grey.50',
            borderStyle: 'dashed',
            borderColor: 'grey.300',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mb: 0.5,
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '0.65rem',
              letterSpacing: '0.5px',
            }}
          >
            Preview
          </Typography>
          <MathTextRenderer
            text={value}
            style={{ fontSize: '0.95rem', lineHeight: 1.6 }}
          />
        </Paper>
      )}

      {/* MathLive Equation Editor Popover */}
      <Popover
        open={mathEditorOpen}
        anchorEl={anchorEl}
        onClose={handleCloseMathEditor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 480,
              maxWidth: '95vw',
              borderRadius: 2,
              boxShadow: 6,
              overflow: 'visible',
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Insert Math Equation
            </Typography>
            <IconButton size="small" onClick={handleCloseMathEditor}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Type or use the toolbar to build your equation. Press Enter to insert.
          </Typography>

          {/* MathLive Field */}
          <Box
            sx={{
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 1.5,
              overflow: 'hidden',
              mb: 1.5,
              '& math-field': {
                fontSize: '1.2rem',
                padding: '12px 16px',
                minHeight: '60px',
                display: 'block',
                width: '100%',
                outline: 'none',
                border: 'none',
                fontFamily: '"KaTeX_Main", "Times New Roman", serif',
              },
              '& math-field::part(virtual-keyboard-toggle)': {
                display: 'none',
              },
            }}
            onKeyDown={handleMathFieldKeyDown}
          >
            <math-field
              ref={mathFieldRef}
              virtual-keyboard-mode="manual"
            />
          </Box>

          {/* LaTeX Preview */}
          {currentLatex && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 1.5,
                bgcolor: 'grey.50',
                borderStyle: 'dashed',
                textAlign: 'center',
              }}
            >
              <MathTextRenderer
                text={`$${currentLatex}$`}
                style={{ fontSize: '1.1rem' }}
              />
            </Paper>
          )}

          <Divider sx={{ mb: 1.5 }} />

          {/* Actions */}
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button size="small" onClick={handleCloseMathEditor}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleInsertEquation}
              disabled={!currentLatex.trim()}
            >
              Insert Equation
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default memo(MathInput);
